export enum IAAgentTypeEnum {
  flow = 'flow',
  system = 'system',
}

export enum IAAgentRoleEnum {
  customer = 'customer',
  admin = 'admin',
}

export enum IAMessageStatusEnum {
  pending = 'pending',
  processing = 'processing',
  completed = 'completed',
  failed = 'failed',
}

export enum IAMessageAuthorEnum {
  user = 'user',
  assistant = 'assistant',
}

export interface ScoreComponentProps {
  score: number
  reason: string
  maxScore: number
}

export interface ExtractionOldProps {
  postalCode: string
  targetPrice: string
  productUsage: string
  monthlyVolume: string
  preferredBrands: string[]
  purchaseFrequency: string
  acceptsCounterTypes: string
  additionalInformation: string
  productsUsuallyBought: string[]
}
export interface ScoringOldProps {
  location: ScoreComponentProps
  products: ScoreComponentProps
  targetPrice: ScoreComponentProps
  productUsage: ScoreComponentProps
  monthlyVolume: ScoreComponentProps
  purchaseFrequency: ScoreComponentProps
  acceptsCounterTypes: ScoreComponentProps
}

export interface FinalScoringOldProps {
  scoring: ScoringOldProps
  success: boolean
  summary: string
  totalScore: number
  missingData: string[]
  maxTotalScore: number
  recommendation: string
}

export interface IAConversationExtraDataQualificationOldProps {
  extraction: ExtractionOldProps
  qualifiedAt: string
  qualifiedBy: string
  finalScoring: FinalScoringOldProps
  lastExtractionAt: string
}
export interface CheckpointProps {
  industry: string | null
  isSample: boolean | null
  quantity: number | null
  isBelowMOQ: boolean | null
  postalCode: string | null
  productType: string | null
  productTitle: string | null
  productUsage: string | null
  quantityUnit: string | null
  missingFields: string[]
  stepsCompleted: string[]
  lastCheckpointAt: string | null
  purchaseFrequency: string | null
  catalogProductUuid: string | null
  acceptsCounterTypes: boolean | null
  productAvailability: string | null
  checkpointInitialized: boolean
}

export interface PropagationProps {
  rfqUuid: string | null
  rfqUpdated: boolean
  isQualified: boolean
  propagatedAt: string | null
  profileUpdated: boolean
  moscowClassification: 'must' | 'should' | 'could' | 'wont' | 'unknown'
}

export interface ScoringProps {
  location: ScoreComponentProps
  quantityMOQ: ScoreComponentProps
  businessContext: ScoreComponentProps
  purchaseFrequency: ScoreComponentProps
  productAvailability: ScoreComponentProps
  // Optional fields for backwards compatibility
  products?: ScoreComponentProps
  productUsage?: ScoreComponentProps
  monthlyVolume?: ScoreComponentProps
  acceptsCounterTypes?: ScoreComponentProps
  targetPrice?: ScoreComponentProps
}

export interface FinalScoringProps {
  scoring: ScoringProps
  success: boolean
  summary: string
  totalScore: number
  maxTotalScore: number
  recommendation: string
  qualificationLevel: string
}

export interface IAConversationExtraDataQualificationProps {
  checkpoint: CheckpointProps
  propagation: PropagationProps
  qualifiedAt: string
  finalScoring: FinalScoringProps
}

export interface IAConversationExtraDataEvaluationProps {
  clarityNote: number
  generalNote: number
  generalNoteObservation: string
  isConversationValid: boolean
  isJtbdCompleted: boolean
  jtbdCompletedObservation: string
  conversationValidationObservation: string
  resolutionSuccessNote: number
  userSentimentNote: number
  agentsObservation: string
  agentEvaluations: {
    agent: string
    isJtbdCompleted: boolean
  }[]
}

export interface IAConversationExtraDataGeneratedProps {
  generatedData: string[]
}

export interface IAConversationExtraDataProps {
  evaluation: IAConversationExtraDataEvaluationProps
  rfq: IAConversationExtraDataGeneratedProps
  leadQualifier?: IAConversationExtraDataQualificationProps
}

export interface IAMessageProps {
  uuid: string
  conversationUuid: string
  author: keyof typeof IAMessageAuthorEnum
  content: string
  status: keyof typeof IAMessageStatusEnum
  createdAt: Date
  messageType: string
  context: string
}

export enum IAConversationRoleEnum {
  customer_in = 'customer_in',
  customer_out = 'customer_out',
  admin = 'admin',
}

export interface IAAgentProps {
  uuid: string
  name: string
  role: keyof typeof IAAgentRoleEnum
  type: keyof typeof IAAgentTypeEnum
  identify: string
  context: string[]
  active: boolean
  createdAt: Date
  updatedAt: Date
  nextAgent: IAAgentProps
  nextAgentUuid: string | null
  diagramPosition: [number, number]
}

export interface IATagProps {
  uuid: string
  slug: string
  description: string
  color: string
  active: boolean
  createdAt: Date
  updatedAt: Date
}

export interface IATagListWithPagesProps {
  count: number
  next?: string
  previous?: string
  results?: IATagProps[]
}

export interface IAAgentListWithPagesProps {
  count: number
  next?: string
  previous?: string
  results?: IAAgentProps[]
}

export interface IAConversationProps {
  uuid: string
  status: string
  createdAt: Date
  updatedAt: Date
  origin: string
  createdBy: string
  extraData: IAConversationExtraDataProps
  messages: IAMessageProps[]
  attendedAt: string
  role: keyof typeof IAConversationRoleEnum
  isAiPaused: boolean
  tags: IATagProps[]
}

export interface IAConversationListWithPagesProps {
  count: number
  next?: string
  previous?: string
  results?: IAConversationProps[]
}

export interface IAConversationMetricsProps {
  generalNoteMetricsReport: {
    countCurrentPeriod: number
    countPreviousPeriod: number
    currentPeriodAvg: number
    deltaPercentage: number
    deltaVariation: number
    previousPeriodAvg: number
  }
  jtbdCompletedMetricsReport: {
    currentPeriodCount: number
    currentPeriodPercentage: number
    deltaPercentage: number
    deltaVariation: number
    previousPeriodCount: number
    previousPeriodPercentage: number
    totalCurrentPeriod: number
    totalPreviousPeriod: number
  }
}

export interface IAReactivationMetaTemplateParamProps {
  paramName: string
  example: string
}

export interface IAReactivationMetaTemplateComponentProps {
  type: string
  text: string
  example?: {
    bodyTextNamedParams: IAReactivationMetaTemplateParamProps[]
  }
}

export interface IAReactivationMetaTemplateProps {
  id: string
  name: string
  language: string
  status: string
  category: string
  components: IAReactivationMetaTemplateComponentProps[]
}

export interface IAReactivationMetaTemplateCreateRequestProps {
  templateId: string
  contacts: Record<string, string>[]
  variableMapping: Record<string, string>
  agentSlug: string
  conversationTagsSlugs: string[]
}

export interface IAReactivationMetaTemplateCreateResponseProps {
  success: number
  failed: number
  total: number
  results: Array<{
    phone: string
    variables: Record<string, string>
    success: boolean
    error?: string
  }>
}
